import { OnDestroy, OnInit, } from '@angular/core';
import { FormBuilder, Validators, } from '@angular/forms';
import { ActivatedRoute, Router, } from '@angular/router';
import { TreeChecklistComponent, } from '@components/ui-tree-checklist/ui-tree-checklist.component';
import { ClientErrorCodes, } from '@models';
import { TranslateService } from '@ngx-translate/core';
import { Logger } from '@services';
import { WebhookService } from '@services/webhook.service';
import { UiSnackBarService } from '@uipath/angular/components/ui-snackbar';
import UiValidators, { WEBHOOKS_SECRET_MAX_LENGTH } from '@validators';
import cloneDeep from 'lodash-es/cloneDeep';
import { BehaviorSubject, of, Subject, throwError, } from 'rxjs';
import { debounceTime, filter, finalize, map, startWith, switchMap, takeUntil, tap, } from 'rxjs/operators';
export var WebHookFormFields;
(function (WebHookFormFields) {
    WebHookFormFields["id"] = "id";
    WebHookFormFields["enabled"] = "enabled";
    WebHookFormFields["url"] = "url";
    WebHookFormFields["allowInsecureSSL"] = "allowInsecureSSL";
    WebHookFormFields["secret"] = "secret";
    WebHookFormFields["subscribeToAllEvents"] = "subscribeToAllEvents";
    WebHookFormFields["events"] = "events";
})(WebHookFormFields || (WebHookFormFields = {}));
var WebhookUpsertPageComponent = /** @class */ (function () {
    function WebhookUpsertPageComponent(_webhookService, _translateService, _fb, _router, _route, _logger, _snack) {
        var _this = this;
        this._webhookService = _webhookService;
        this._translateService = _translateService;
        this._fb = _fb;
        this._router = _router;
        this._route = _route;
        this._logger = _logger;
        this._snack = _snack;
        this.loading$ = new BehaviorSubject(true);
        this.cantConnect$ = new BehaviorSubject(false);
        this.badSSL$ = new BehaviorSubject(false);
        this.isEditMode = false;
        this.fields = WebHookFormFields;
        this._webhook = {
            id: NaN,
            url: '',
            enabled: true,
            allowInsecureSSL: false,
            subscribeToAllEvents: true,
            events: [],
        };
        this._destroyed$ = new Subject();
        this._initForm = function (webhook) {
            var _a;
            var formFields = (_a = {},
                _a[WebHookFormFields.id] = [
                    webhook.id,
                ],
                _a[WebHookFormFields.enabled] = [
                    webhook.enabled,
                ],
                _a[WebHookFormFields.url] = [
                    webhook.url,
                    {
                        validators: [
                            Validators.required,
                            UiValidators.httpUrl(false),
                        ],
                    },
                ],
                _a[WebHookFormFields.allowInsecureSSL] = [
                    webhook.allowInsecureSSL,
                ],
                _a[WebHookFormFields.secret] = [
                    webhook.secret,
                    Validators.compose([
                        Validators.maxLength(WEBHOOKS_SECRET_MAX_LENGTH),
                    ]),
                ],
                _a[WebHookFormFields.subscribeToAllEvents] = [
                    webhook.subscribeToAllEvents,
                    Validators.compose([
                        Validators.required,
                    ]),
                ],
                _a[WebHookFormFields.events] = [
                    webhook.events,
                ],
                _a);
            _this.formGroup = _this._fb.group(formFields, {
                validator: _this._selectedEventsValidator,
            });
            _this._urlOnChangeValidator$()
                .subscribe(function (response) {
                _this.cantConnect$.next(!response.canConnect);
                _this.badSSL$.next(!!response.hasBadSsl);
            });
            _this.preselectedNodes = _this._editGroups;
        };
        this._selectedEventsValidator = function (form) {
            if (!form.get(WebHookFormFields.subscribeToAllEvents).value &&
                form.get(WebHookFormFields.events).value.length === 0) {
                return {
                    noSelectedEvents: true,
                };
            }
            return null;
        };
        this.eventGroups$ = this._webhookService.getEventTypeGroups();
    }
    WebhookUpsertPageComponent.prototype.ngOnInit = function () {
        var _this = this;
        this._contextData$
            .pipe(tap(function () { return _this.loading$.next(false); }), tap(this._initForm), takeUntil(this._destroyed$))
            .subscribe(function () { }, function (e) {
            if (typeof e === 'string') {
                _this._logger.error(e);
            }
            _this._logger.error(e);
            _this._router.navigateByUrl('/webhooks');
        });
    };
    WebhookUpsertPageComponent.prototype.ngOnDestroy = function () {
        this._destroyed$.next();
    };
    WebhookUpsertPageComponent.prototype.clipboardCopied = function () {
        this._snack.success(this._translateService.instant('global_message_copied_clipboard'));
    };
    WebhookUpsertPageComponent.prototype.onSelectEvent = function (selectedNodes) {
        this.formGroup.get(WebHookFormFields.events).setValue(selectedNodes
            .filter(function (node) { return node.level !== 0; })
            .map(function (node) { return node.item; }));
    };
    WebhookUpsertPageComponent.prototype.onSubmit = function () {
        var _this = this;
        if (this.formGroup.invalid ||
            this.loading$.value) {
            return;
        }
        this.loading$.next(true);
        var payload = cloneDeep(this.formGroup.value);
        payload.id = payload.id ? payload.id : void 0;
        payload.events = payload.subscribeToAllEvents ? [] : payload.events;
        (this.isEditMode ?
            this._webhookService.update(this._webhook.id, payload) :
            this._webhookService.add(payload)).pipe(finalize(function () { return _this.loading$.next(false); })).subscribe(function () { return _this._router.navigate(['/webhooks']); });
    };
    Object.defineProperty(WebhookUpsertPageComponent.prototype, "_contextData$", {
        get: function () {
            var _this = this;
            var id = this._route.snapshot.paramMap.get('id');
            this.isEditMode = id !== null;
            if (!this.isEditMode) {
                return of(this._webhook);
            }
            var webhookId = parseInt(id, 10);
            if (!(webhookId > 0)) {
                return throwError(this._translateService.instant('invalid_webhook_ID') + " (#" + ClientErrorCodes.InvalidWebHookID + ")");
            }
            return this._webhookService
                .getInstance(webhookId)
                .pipe(tap(function (model) { return _this._webhook = model; }));
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(WebhookUpsertPageComponent.prototype, "_editGroups", {
        get: function () {
            return this._webhook.events.reduce(function (accumulator, current) {
                var entity = current.split('.')[0];
                accumulator[entity] = (accumulator[entity] || []).concat([current]);
                return accumulator;
            }, {});
        },
        enumerable: true,
        configurable: true
    });
    WebhookUpsertPageComponent.prototype._urlOnChangeValidator$ = function () {
        var _this = this;
        return this.formGroup.get(WebHookFormFields.url)
            .valueChanges
            .pipe(startWith(this.formGroup.get(WebHookFormFields.url).value), debounceTime(300), tap(function () {
            _this.cantConnect$.next(false);
            _this.badSSL$.next(false);
        }), filter(function () { return !_this.formGroup.get(WebHookFormFields.url).invalid; }), filter(function (val) { return !!val; }), map(function (val) { return val.trim(); }), switchMap(function (val) { return _this._webhookService.verifyHost(val); }), takeUntil(this._destroyed$));
    };
    return WebhookUpsertPageComponent;
}());
export { WebhookUpsertPageComponent };
